/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Seat Limit Badge Component
 * Displays family organization seat usage with visual indicators
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { Users } from 'lucide-react'

const SeatLimitBadge = ({
  used,
  max,
  type = 'children',
  members,
  pendingInvitations,
  className = ''
}) => {
  const percentage = max > 0 ? (used / max) * 100 : 0

  // Color coding based on usage percentage
  const getColorClasses = () => {
    if (percentage >= 100) {
      return {
        bg: 'bg-error-100 dark:bg-error-900/30',
        text: 'text-error-800 dark:text-error-300',
        border: 'border-error-300 dark:border-error-700',
        progress: 'bg-error-500'
      }
    } else if (percentage >= 80) {
      return {
        bg: 'bg-warning-100 dark:bg-warning-900/30',
        text: 'text-warning-800 dark:text-warning-300',
        border: 'border-warning-300 dark:border-warning-700',
        progress: 'bg-warning-500'
      }
    }
    return {
      bg: 'bg-success-100 dark:bg-success-900/30',
      text: 'text-success-800 dark:text-success-300',
      border: 'border-success-300 dark:border-success-700',
      progress: 'bg-success-500'
    }
  }

  const colors = getColorClasses()
  const displayType = type === 'children' ? 'Children' : 'Guardians'

  // Build tooltip text with breakdown
  const hasBreakdown = members !== undefined && pendingInvitations !== undefined
  const tooltipText = hasBreakdown
    ? `${members} active • ${pendingInvitations} pending invitation${pendingInvitations !== 1 ? 's' : ''}`
    : `${used} of ${max} seats used`

  return (
    <div className={`inline-flex flex-col gap-2 ${className}`} title={tooltipText}>
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${colors.bg} ${colors.border}`}
      >
        <Users className={`w-4 h-4 ${colors.text}`} />
        <span className={`text-sm font-semibold ${colors.text}`}>
          {used}/{max} {displayType}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${colors.progress}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Breakdown text (if available) */}
      {hasBreakdown && (members > 0 || pendingInvitations > 0) && (
        <div className="text-xs text-gray-600 dark:text-gray-400">
          {members > 0 && <span>{members} active</span>}
          {members > 0 && pendingInvitations > 0 && <span> • </span>}
          {pendingInvitations > 0 && <span>{pendingInvitations} pending</span>}
        </div>
      )}
    </div>
  )
}

export default SeatLimitBadge
